// frontend/src/index.js
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';

// Variables d'environnement
console.log('🚀 Farm-Booking Frontend démarrage...');
console.log('API URL:', process.env.REACT_APP_API_URL || '/api');
console.log('Environnement:', process.env.NODE_ENV);

// Point d'entrée principal
const root = ReactDOM.createRoot(document.getElementById('root'));

// Gestion d'erreur React
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('React Error Boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full text-center">
            <div className="text-6xl mb-4">🚨</div>
            <h1 className="text-xl font-bold text-red-600 mb-2">
              Erreur Application
            </h1>
            <p className="text-gray-600 mb-4">
              Une erreur inattendue s'est produite dans l'interface.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Recharger l'application
            </button>
            <div className="mt-4 text-xs text-gray-500">
              <details>
                <summary className="cursor-pointer">Détails technique</summary>
                <pre className="mt-2 text-left bg-gray-100 p-2 rounded text-xs">
                  {this.state.error?.toString()}
                </pre>
              </details>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Test de compatibilité navigateur
const checkBrowserCompatibility = () => {
  const requiredFeatures = [
    'fetch',
    'Promise',
    'localStorage',
    'JSON'
  ];
  
  const missingFeatures = requiredFeatures.filter(feature => !(feature in window));
  
  if (missingFeatures.length > 0) {
    document.getElementById('root').innerHTML = `
      <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 20px; background: #fef2f2;">
        <div style="background: white; padding: 24px; border-radius: 12px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); text-align: center; max-width: 400px;">
          <div style="font-size: 48px; margin-bottom: 16px;">⚠️</div>
          <h1 style="color: #dc2626; font-weight: bold; margin-bottom: 12px;">Navigateur non compatible</h1>
          <p style="color: #6b7280; margin-bottom: 16px;">
            Votre navigateur ne prend pas en charge certaines fonctionnalités requises.
          </p>
          <p style="color: #6b7280; font-size: 14px; margin-bottom: 16px;">
            Fonctionnalités manquantes: ${missingFeatures.join(', ')}
          </p>
          <p style="color: #6b7280; font-size: 14px;">
            Veuillez mettre à jour votre navigateur ou utiliser Chrome, Firefox, Safari ou Edge récents.
          </p>
        </div>
      </div>
    `;
    return false;
  }
  
  return true;
};

// Test de connectivité API
const checkAPIConnection = async () => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || '/api';
    const response = await fetch(`${apiUrl}/health`, { 
      method: 'GET',
      signal: AbortSignal.timeout(5000)
    });
    
    if (!response.ok) {
      throw new Error(`API répond avec status ${response.status}`);
    }
    
    console.log('✅ Connexion API établie');
    return true;
    
  } catch (error) {
    console.warn('⚠️ Problème connexion API:', error);
    
    // Afficher warning mais ne pas bloquer l'app
    const warningDiv = document.createElement('div');
    warningDiv.innerHTML = `
      <div style="background: #fbbf24; color: #92400e; padding: 8px 16px; text-align: center; font-size: 14px; border-bottom: 1px solid #f59e0b;">
        ⚠️ Connexion API limitée - Certaines fonctionnalités peuvent ne pas fonctionner
      </div>
    `;
    document.body.prepend(warningDiv);
    
    // Masquer après 5 secondes
    setTimeout(() => {
      warningDiv.remove();
    }, 5000);
    
    return false;
  }
};

// Fonction de démarrage
const startApp = async () => {
  // Vérifications préliminaires
  if (!checkBrowserCompatibility()) {
    return;
  }
  
  // Test API en arrière-plan (non bloquant)
  checkAPIConnection();
  
  // Masquer le loading HTML
  const loadingElement = document.getElementById('loading');
  if (loadingElement) {
    setTimeout(() => {
      loadingElement.style.display = 'none';
    }, 300);
  }
  
  // Démarrage React
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </React.StrictMode>
  );
  
  console.log('✅ Farm-Booking Frontend prêt');
};

// Démarrage de l'application
startApp();

// Gestion des erreurs non capturées
window.addEventListener('unhandledrejection', (event) => {
  console.error('Promise rejetée non gérée:', event.reason);
  
  // Ne pas afficher d'erreur pour les erreurs réseau communes
  if (event.reason?.name === 'AbortError' || 
      event.reason?.message?.includes('fetch')) {
    return;
  }
  
  // Afficher notification discrète
  const errorDiv = document.createElement('div');
  errorDiv.innerHTML = `
    <div style="position: fixed; top: 20px; right: 20px; background: #dc2626; color: white; padding: 12px 16px; border-radius: 8px; z-index: 1000; font-size: 14px; max-width: 300px;">
      <div style="font-weight: bold; margin-bottom: 4px;">Erreur système</div>
      <div>${event.reason?.message || 'Erreur inconnue'}</div>
      <button onclick="this.parentElement.remove()" style="position: absolute; top: 4px; right: 8px; background: none; border: none; color: white; cursor: pointer; font-size: 16px;">×</button>
    </div>
  `;
  document.body.appendChild(errorDiv);
  
  // Auto-suppression après 5 secondes
  setTimeout(() => {
    if (errorDiv.parentElement) {
      errorDiv.remove();
    }
  }, 5000);
});

// Hot reload pour le développement
if (module.hot) {
  module.hot.accept();
}